import React, { useCallback, useContext } from "react";
import useProductName from "brand-customization/useProductName";
import { Col, Form, ButtonToolbar, Button, Alert, Input } from "components/bootstrap";
import { FormikFormGroup } from "components/common";
import { Headline } from "components/common/Section/SectionComponent";
import { validateField, formHasErrors } from "util/FormsUtils";
import { OidcBackendMetaContext } from "authentication/components/oidc/config/components/OidcBackendMetaProvider";
import SelectionTypeOptions from "authentication/components/directoryServices/SelectionTypeOptions";
import SelectedGroupsOverview from "authentication/components/directoryServices/SelectedGroupsOverview";
import OktaMatchingGroupsOverview from "authentication/components/oidc/config/components/OktaMatchingGroupsOverview";
import payloadFromFormValues from "authentication/components/oidc/config/helpers/payloadFromFormValues";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
export const formValidation = (synchronizeGroups, selectionType) => {
  let teamSelection;
  let oktaApiToken;
  if (synchronizeGroups) {
    if (selectionType !== "all") {
      teamSelection = {
        required: true
      };
    }
    oktaApiToken = {
      required: true
    };
  }
  const validation = {
    oktaApiToken,
    teamDefaultRoles: {},
    teamSelectionType: {},
    teamSelection
  };
  return validation;
};
const GroupSyncForm = ({
  values,
  setFieldValue,
  isSubmitting,
  handleSubmit,
  validateForm,
  hasApiToken,
  submitAllError,
  goToPrevious
}) => {
  const productName = useProductName();
  const _formValidation = useCallback(
    (synchronizeGroups, teamSelectionType) => formValidation(synchronizeGroups, teamSelectionType),
    []
  );
  const validation = _formValidation(values.synchronizeGroups, values.teamSelectionType);
  const sendTelemetry = useSendTelemetry("oidc-groupsync-step");
  const _onSubmitAll = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.OIDC_GROUP_SYNC_SAVE_CLICKED, {
      app_action_value: "save-service"
    });
    if (values.synchronizeGroups) {
      return validateForm().then((errors) => {
        if (!formHasErrors(errors)) {
          handleSubmit();
        }
      });
    }
    return handleSubmit();
  };
  const _onGroupSelect = (groupId) => {
    const selectedGroupsIds = values.teamSelection;
    if (selectedGroupsIds == null ? void 0 : selectedGroupsIds.includes(groupId)) {
      setFieldValue("teamSelection", selectedGroupsIds.remove(groupId));
    } else {
      setFieldValue("teamSelection", selectedGroupsIds.add(groupId));
    }
  };
  const { licenseIsValid } = useContext(OidcBackendMetaContext);
  const disableGroupSyncStep = !licenseIsValid;
  const disableForm = disableGroupSyncStep || !values.synchronizeGroups;
  return /* @__PURE__ */ React.createElement(Col, { md: 7 }, /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info", title: "Introduction" }, "This step enables importing Okta groups as ", productName, " teams. The group synchronization can be activated by clicking on the checkbox with the label ", /* @__PURE__ */ React.createElement("i", null, "Enable Group Synchronization"), ". Afterwards fill out the form and test your settings in the second section ", /* @__PURE__ */ React.createElement("i", null, "Load matching groups"), ". The last section", " ", /* @__PURE__ */ React.createElement("i", null, "Select groups to import"), " allows you to specify which groups you want to import."), /* @__PURE__ */ React.createElement(
    Input,
    {
      label: "Enable Group Synchronization",
      id: "synchronize-groups-checkbox",
      disabled: disableGroupSyncStep,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        label: "Synchronize Groups",
        disabled: disableGroupSyncStep,
        wrapperClassName: "col-sm-12",
        name: "synchronizeGroups",
        help: "Select this option if you want to import groups as teams",
        type: "checkbox"
      }
    )
  ), hasApiToken && values.oktaApiToken === void 0 ? /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "oktaApiToken",
      label: "Okta API Token",
      help: "Add an Okta API token used to sync groups.",
      labelClassName: "col-sm-3",
      disabled: disableForm,
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("oktaApiToken", "") }, "Reset Token")
  ) : /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      buttonAfter: hasApiToken && values.oktaApiToken !== void 0 ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("oktaApiToken", void 0) }, "Undo Reset") : void 0,
      label: "Okta API Token",
      disabled: disableForm,
      required: true,
      name: "oktaApiToken",
      placeholder: "Okta API Token",
      validate: validateField(validation.oktaApiToken),
      help: "Your Okta API token.",
      type: "password"
    }
  ), /* @__PURE__ */ React.createElement(Headline, null, "Load matching groups"), /* @__PURE__ */ React.createElement(
    OktaMatchingGroupsOverview,
    {
      disabled: disableForm,
      prepareSubmitPayload: payloadFromFormValues,
      onGroupSelect: _onGroupSelect
    }
  ), /* @__PURE__ */ React.createElement(Headline, null, "Select groups to import"), /* @__PURE__ */ React.createElement(SelectionTypeOptions, { disabled: disableForm }), /* @__PURE__ */ React.createElement(SelectedGroupsOverview, { onGroupSelect: _onGroupSelect, validation })), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.OIDC_PREVIOUS_SERVER_CONFIG_CLICKED, {
          app_action_value: "server-configuration-button"
        });
        goToPrevious();
      },
      type: "button"
    },
    "Previous: Server Configuration"
  ), /* @__PURE__ */ React.createElement(Button, { disabled: isSubmitting, onClick: _onSubmitAll }, "Finish & Save Service"))));
};
export default GroupSyncForm;
